import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AdminPageHeader,
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonTypes,
  ButtonWidth,
  Drawer,
  DropDown,
  Label,
  MLPVersion,
  Modal,
  TableWithSearchTemplate,
  Theme,
} from '../../../ui/components';
import { LoggerService, dateTimeToCustomFormat } from '../../../ui/utils';
import { useAdminToast } from '../../../ui/components/AdminToast/AdminToast';
import { DropDownData } from '../../../ui/components/Form/DropDown/types/propsType';
import { DynamicObjectInterface } from '../../../sdk/type';
import { useAPICall } from '../../../hooks';
import EnrollmentService from '../../../sdk/services/enrollmentService';
import CampaignService from '../../../sdk/services/campaignService';
import OperationCenterErrorCard, {
  ErrorType,
} from '../../../ui/components/OperationCenterErrorCard/OperationCenterErrorCard';
import getCoachEnrollmentListConfig, { dataSearchOption } from './coachEnrollmentListConfig';
import styles from './index.module.scss';

export const AdminCoachEnrollmentList = () => {
  interface CoachEnrollmentData {
    recId: string;
    campaignId: string;
    campaignName: string;
    validity: number;
    coachId: string;
    aboId: string;
    enrollmentType: string;
    name: string;
    pin: string;
    directLeaderName: string;
    directLeader: string;
    directLeaderPin: string;
    enrollmentDate: Date;
    endDate: Date;
    status: string;
    remark: string;
    rollingMonth: string;
  }

  interface CampaignOption {
    campaignId: string;
    campaignName: string;
    campaignStart: string;
    campaignEnd: string;
    status: string;
    title: string;
    value: string;
  }

  interface MasterData {
    campaign: DynamicObjectInterface[];
  }

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedRow, setSelectedRow] = useState<CoachEnrollmentData | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [data, setData] = useState([]);
  const [masterData, setMasterData] = useState<MasterData | null>();
  const [campaignList, setCampaignList] = useState<CampaignOption[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignOption>();
  const [displayEmpty, setDisplayEmpty] = useState(false);

  const { adminToast } = useAdminToast();

  const apiCall = useAPICall();

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (masterData) {
      setCampaignList(
        masterData.campaign
          ?.map(
            c =>
              ({
                ...c,
                title: `${c.campaignId} - ${c.campaignName}`,
                value: c.campaignId ?? '',
              }) as CampaignOption,
          )
          .sort((a, b) => {
            if (a.campaignStart > b.campaignStart) return -1;
            if (a.campaignStart < b.campaignStart) return 1;
            return 0;
          }),
      );
    }
  }, [masterData]);

  useEffect(() => {
    if (campaignList?.length) {
      const params = new URLSearchParams(location.search);
      const campaignId = params.get('campaignId') ?? '';
      if (campaignId) {
        setSelectedCampaign(prev => {
          let _selectedCampaign = campaignList.find(c => c.campaignId === campaignId);
          if (_selectedCampaign) return { ..._selectedCampaign };
          else return prev;
        });
      } else {
        let latest: CampaignOption = campaignList.reduce((latest: DynamicObjectInterface, current) => {
          const currentStartDate = new Date(current.campaignStart);
          const latestStartDate = latest ? new Date(latest.campaignStart) : null;

          if (!latestStartDate || currentStartDate > latestStartDate) return { ...current, value: current.campaignId };
          else return latest;
        }, null);
        navigate(`?campaignId=${latest.value}`, { replace: true });
      }
    }
  }, [campaignList, location]);

  useEffect(() => {
    if (selectedCampaign) {
      fetchCoachEnrollmentList();
    }
  }, [selectedCampaign]);

  const fetchMasterData = async () => {
    let list = {
      campaign: {
        endpoint: CampaignService.getCampaigns,
      },
    };
    try {
      const results = await Promise.all(Object.values(list)?.map(l => apiCall(l.endpoint)));

      setMasterData(
        Object.keys(list).reduce((result: DynamicObjectInterface, key, idx) => {
          result[key] = results[idx]?.data?.data?.filter(
            (campaign: DynamicObjectInterface) => campaign.campaignType === 'CHALLENGE'
          );
          return result;
        }, {}),
      );
    } catch (error) {
      LoggerService.log('Failed to retrieve campaign list', error);
    }
  };

  const fetchCoachEnrollmentList = async () => {
    try {
      const response = await apiCall(EnrollmentService.getEnrollmentList, 'COACH');
      const allData = response?.data?.data ?? [];
      // Filter by selected campaign
      const filteredData = allData.filter(
        (item: CoachEnrollmentData) => item.campaignId === selectedCampaign?.campaignId,
      );
      setData(filteredData);
      setDisplayEmpty(filteredData.length === 0);
    } catch (error) {
      LoggerService.log('Failed to retrieve coach enrollment list', error);
      setDisplayEmpty(true);
    }
  };

  const onEdit = (id: string) => {
    navigate(`/admin/coach-enrollment-configuration/edit?=${id}`);
  };

  const onDelete = (row?: DynamicObjectInterface) => {
    if (row) setSelectedRow(row);
    setShowDelete(true);
  };

  const exportToCSV = async () => {
    try {
      const response = await apiCall(EnrollmentService.getExportEnrollmentCsv, `COACH`);

      if (response.status === 200) {
        const data = response.data;
        const blob = new Blob([data], { type: 'text/csv' });

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', 'coachEnrollment.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Release the object URL resources
        URL.revokeObjectURL(link.href);
      }
    } catch (error) {
      LoggerService.log('Error exporting CSV', error);
    }
  };

  const buttonConfig = [
    {
      title: t('admin.coachEnrollmentMaster.addCoachEnrollment'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => {
        navigate(`/admin/coach-enrollment-configuration/edit?=new`);
      },
    },
  ];

  const exportButtonConfig = [
    {
      title: t('admin.coachMaster.exportCSV'),
      buttonStyle: ButtonStyles.OUTLINE,
      onClick: exportToCSV,
    },
  ];

  const handleInputChange = (value: DynamicObjectInterface) => {
    navigate(`?campaignId=${value.campaignId}`);
  };

  const onDeleteAction = async () => {
    try {
      const response = await apiCall(EnrollmentService.deleteEnrollment, `${selectedRow?.recId}`);
      if (response?.status === 200) {
        LoggerService.log('Coach Enrollment deleted successfully');
        adminToast({
          message: 'Coach Enrollment deleted successfully',
          theme: Theme.SUCCESS,
        });
        fetchCoachEnrollmentList();
        setDrawerOpen(false);
      } else {
        LoggerService.log('Failed to delete coach enrollment');
      }
    } catch (error) {
      LoggerService.log('Error deleting coach enrollment', error);
    } finally {
      // Close the modal whether the deletion was successful or not
      setShowDelete(false);
    }
  };

  const drawerDetails = (
    <>
      {drawerOpen && (
        <Drawer
          onClose={() => setDrawerOpen(false)}
          closeOnBackdropClick={true}
          closeIcon={true}
          header={<div>Details</div>}
          bottomGapFooter="0px"
          footer={
            <div className={styles['button-container']}>
              <Button
                title="Delete"
                onClick={() => onDelete()}
                size={ButtonSize.SMALL}
                buttonWidth={ButtonWidth.FULL_WIDTH}
              />
              <Button
                title="Edit"
                onClick={() => onEdit(selectedRow?.recId ?? '')}
                size={ButtonSize.SMALL}
                buttonWidth={ButtonWidth.FULL_WIDTH}
              />
            </div>
          }
        >
          <div className={styles['summary-pane-body']}>
            <div className={styles['basic-details']}>
              <div className={styles['title-value']}>
                <div className={styles['two-columns']}>
                  <Label title={t('admin.coachEnrollmentMaster.campaignId')} value={selectedRow?.campaignId} />
                  <Label title={t('admin.coachEnrollmentMaster.campaignName')} value={selectedRow?.campaignName} />
                </div>
                {/* <Label
                  title={t('admin.coachEnrollmentMaster.rollingMonth')}
                  value={selectedRow?.validity?.toString() ?? '0'}
                /> */}
              </div>
              <hr className={styles['horizontal-line']} />
              <div className={styles['title-value']}>
                <div className={styles['two-columns']}>
                  <Label title={t('admin.coachEnrollmentMaster.coachId')} value={selectedRow?.coachId} />
                  <Label title={t('admin.coachEnrollmentMaster.coachAboId')} value={selectedRow?.aboId} />
                </div>
                <div className={styles['two-columns']}>
                  <Label title={t('admin.coachEnrollmentMaster.coachName')} value={selectedRow?.name} />
                  <Label title={t('admin.coachEnrollmentMaster.coachPin')} value={selectedRow?.pin} />
                </div>
                <div className={styles['two-columns']}>
                  <Label title={t('admin.coachEnrollmentMaster.directLeader')} value={selectedRow?.directLeader} />
                  <Label
                    title={t('admin.coachEnrollmentMaster.directLeaderName')}
                    value={selectedRow?.directLeaderName}
                  />
                </div>
                <Label title={t('admin.coachEnrollmentMaster.directLeaderPin')} value={selectedRow?.directLeaderPin} />
              </div>
              <hr className={styles['horizontal-line']} />
              {/* <div className={styles['title-value']}>
                <Label
                  title={t('admin.coachEnrollmentMaster.enrollmentRollingMonth')}
                  value={selectedRow?.rollingMonth ?? selectedRow?.validity?.toString() ?? '-'}
                />
                <Label
                  title={t('admin.coachEnrollmentMaster.enrollmentDate')}
                  value={
                    selectedRow?.enrollmentDate
                      ? dateTimeToCustomFormat(selectedRow?.enrollmentDate?.toString(), 'MMM YYYY')
                      : ''
                  }
                />
                <Label
                  title={t('admin.coachEnrollmentMaster.period')}
                  value={
                    selectedRow?.enrollmentDate && selectedRow?.endDate
                      ? dateTimeToCustomFormat(selectedRow?.enrollmentDate?.toString(), 'MMM YYYY') +
                        ' to ' +
                        dateTimeToCustomFormat(selectedRow?.endDate?.toString(), 'MMM YYYY')
                      : ''
                  }
                />
              </div> */}
              <Label title={t('admin.coachEnrollmentMaster.remark')} value={selectedRow?.remark} />
            </div>
          </div>
        </Drawer>
      )}
    </>
  );

  const deletePrompt = (
    <>
      {showDelete && (
        <Modal
          primaryButtonLabel={''}
          headline={`${t('admin.coachEnrollmentMaster.deleteCoachEnrollment')}`}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('admin.coachEnrollmentMaster.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                onDeleteAction();
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('admin.coachEnrollmentMaster.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowDelete(false);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );

  return (
    <div>
      <AdminPageHeader
        title={t('admin.coachEnrollmentMaster.title')}
        button={[...buttonConfig, ...exportButtonConfig]}
      />
      <div className={styles['coach-list-top']}>
        <DropDown
          name="campaignId"
          dropdownLabel={`${t('admin.teams.campaign')}`}
          disabledFilter={false}
          mlpVersion={MLPVersion.ONE}
          value={selectedCampaign?.value}
          data={campaignList as unknown as DropDownData[]}
          configData={{
            label: 'title',
            onSelect: (selectedOption: DropDownData) => {
              handleInputChange(selectedOption);
            },
          }}
          readOnly={false}
        />
        <div className={styles['coach-list-top-layout']}>
          <div className={styles['coach-list-top-info']}>
            <div>{t('admin.generalCoach.period')}:</div>
            <p>
              {selectedCampaign?.campaignStart && selectedCampaign.campaignEnd
                ? `${dateTimeToCustomFormat(selectedCampaign.campaignStart, 'DD MMM YYYY')} - ${dateTimeToCustomFormat(
                    selectedCampaign.campaignEnd,
                    'DD MMM YYYY',
                  )}`
                : '-'}
            </p>
          </div>
          <div className={styles['coach-list-top-info']}>
            <div>{t('admin.generalCoach.status')}:</div>
            <p>{selectedCampaign?.status ?? '-'}</p>
          </div>
        </div>
      </div>
      {displayEmpty ? (
        <div className={styles['no-results-container']}>
          <OperationCenterErrorCard errorType={ErrorType.NO_RECORD} />
        </div>
      ) : (
        <TableWithSearchTemplate
          dataSource={data}
          onReset={() => void 0}
          searchByStrip
          getColumns={getCoachEnrollmentListConfig}
          data={dataSearchOption}
          rowKey="recId"
          selected={selectedRow?.recId}
          onRowClick={row => {
            setSelectedRow(row);
            setDrawerOpen(true);
          }}
          cb={{ onEdit, onDelete }}
        />
      )}
      {drawerDetails}
      {deletePrompt}
    </div>
  );
};
